import { Product } from '../types';
import { productStore } from '../productStore';

export class ProductService {
  /**
   * Get all products from the shared store
   */
  public getAll(): Product[] {
    return productStore.getProducts();
  }

  /**
   * Get active products for the customer website
   */
  public getActive(): Product[] {
    return productStore.getProducts().filter((p) => p.status === 'Active');
  }

  /**
   * Get product by ID
   */
  public getById(id: string): Product | undefined {
    return productStore.getProductById(id);
  }

  /**
   * Get product by slug
   */
  public getBySlug(slug: string): Product | undefined {
    return productStore.getProducts().find((p) => p.slug === slug || p.id === slug);
  }

  /**
   * Get featured active products for homepage & featured collections
   */
  public getFeatured(): Product[] {
    return productStore
      .getProducts()
      .filter((p) => p.featured && p.status === 'Active');
  }

  /**
   * Get products by category slug or category name
   */
  public getByCategory(categoryNameOrSlug: string): Product[] {
    if (!categoryNameOrSlug || categoryNameOrSlug === 'all') {
      return this.getActive();
    }
    const lower = categoryNameOrSlug.toLowerCase();
    return this.getActive().filter((p) => {
      const catLower = (p.category || '').toLowerCase();
      return catLower === lower || catLower.includes(lower) || lower.includes(catLower);
    });
  }

  /**
   * Search active products automatically indexing Name, Description, Category, Fragrance notes, and Tags.
   */
  public search(query: string): Product[] {
    if (!query || query.trim() === '') return this.getActive();
    const q = query.toLowerCase().trim();

    return this.getActive().filter((p) => {
      const nameMatch = p.name.toLowerCase().includes(q);
      const descMatch =
        (p.description || '').toLowerCase().includes(q) ||
        (p.fullDescription || '').toLowerCase().includes(q) ||
        (p.shortDescription || '').toLowerCase().includes(q);
      const catMatch = (p.category || '').toLowerCase().includes(q);

      const notesMatch =
        (p.notes || '').toLowerCase().includes(q) ||
        (p.topNotes || []).some((n) => n.toLowerCase().includes(q)) ||
        (p.heartNotes || []).some((n) => n.toLowerCase().includes(q)) ||
        (p.baseNotes || []).some((n) => n.toLowerCase().includes(q));

      const tagsMatch = (p.tags || []).some((t) => t.toLowerCase().includes(q));

      return nameMatch || descMatch || catMatch || notesMatch || tagsMatch;
    });
  }

  /**
   * Create a new product
   */
  public create(productData: Partial<Product>): Product {
    const id = productData.id || `product-${Date.now()}`;
    const slug = productData.slug || productData.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || id;
    const now = new Date().toISOString();

    const newProduct: Product = {
      id,
      name: productData.name || 'Untitled Candle',
      slug,
      description: productData.description || productData.fullDescription || '',
      shortDescription: productData.shortDescription || productData.tagline || '',
      fullDescription: productData.fullDescription || productData.description || '',
      price: productData.price ?? 65,
      salePrice: productData.salePrice,
      stock: productData.stock ?? 25,
      category: productData.category || 'Woody & Amber',
      featured: productData.featured ?? false,
      status: productData.status || 'Active',
      images: productData.images && productData.images.length > 0 ? productData.images : [
        {
          id: `img-${id}-cover`,
          url: productData.productImage || 'https://images.unsplash.com/photo-1603006905003-be475563bc59?auto=format,compress&fit=crop&w=800&q=75',
          cover: true,
          order: 0,
        }
      ],
      video: productData.video,
      createdAt: productData.createdAt || now,
      updatedAt: now,
      sku: productData.sku || `NOOR-${Math.floor(100 + Math.random() * 900)}`,
      notes: productData.notes,
      burnTime: productData.burnTime,
      waxType: productData.waxType,
      weight: productData.weight,
      tagline: productData.tagline || productData.shortDescription,
      topNotes: productData.topNotes,
      heartNotes: productData.heartNotes,
      baseNotes: productData.baseNotes,
      wickType: productData.wickType,
      mood: productData.mood,
      colorHex: productData.colorHex,
      flameColorHex: productData.flameColorHex,
      accentColor: productData.accentColor,
      imageBg: productData.imageBg,
      vibeStory: productData.vibeStory,
      tags: productData.tags,
    };

    return productStore.saveProduct(newProduct);
  }

  /**
   * Update an existing product
   */
  public update(id: string, productData: Partial<Product>): Product {
    const existing = productStore.getProductById(id);
    if (!existing) {
      return this.create({ ...productData, id });
    }

    const updated: Product = {
      ...existing,
      ...productData,
      id,
      updatedAt: new Date().toISOString(),
    };

    return productStore.saveProduct(updated);
  }

  /**
   * Delete a product
   */
  public delete(id: string): void {
    productStore.deleteProduct(id);
  }

  /**
   * Duplicate a product
   */
  public duplicate(id: string): Product | undefined {
    return productStore.duplicateProduct(id);
  }

  /**
   * Subscribe to live product store updates
   */
  public subscribe(listener: () => void): () => void {
    return productStore.subscribe(listener);
  }
}

export const productService = new ProductService();
